import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

import {
  CURSOR_QUERY_PARAM,
  DataTable,
  LIMIT_QUERY_PARAM,
} from '@/components/ui/data-table';
import { TableTitle } from '@/components/ui/table-title';
import { AdminMcp, SeekPage } from '@activepieces/shared';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminMcpsTable } from '@/features/internal-admin/lib/admin-mcps-table';
import { UserDetailContext } from '../user-detail';

const UserMcpsTab = () => {
  const { columns, filters } = useAdminMcpsTable();
  const filteredFilters = filters.filter(
    (f) =>
      f.accessorKey !== 'externalIds' && f.accessorKey !== 'externalCustomerId',
  );
  const navigate = useNavigate();
  const { userId, tenantId } = useOutletContext<UserDetailContext>();
  const [searchParams] = useSearchParams();

  const { data, isLoading, isError } = useQuery<SeekPage<AdminMcp>, Error>({
    queryKey: ['admin-user-mcps', userId, searchParams.toString()],
    queryFn: () =>
      adminApi.listMcps({
        cursor: searchParams.get(CURSOR_QUERY_PARAM) ?? undefined,
        limit: Number(searchParams.get(LIMIT_QUERY_PARAM)) || 20,
        search: searchParams.get('name') ?? undefined,
        userId,
        tenantId,
      }),
  });

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Failed to load MCPs')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <TableTitle>{t('MCPs')}</TableTitle>
      <DataTable
        columns={columns}
        page={data}
        isLoading={isLoading}
        filters={filteredFilters}
        hiddenColumns={['externalIds', 'externalCustomerId']}
        onRowClick={(row, newWindow) => {
          const url = `/internal-admin/mcps/${row.tenantId}/${row.id}`;
          if (newWindow) {
            window.open(url, '_blank');
          } else {
            navigate(url);
          }
        }}
      />
    </div>
  );
};

export default UserMcpsTab;
